import { useCallback, useEffect, useRef, useState } from "react";
import { checkEmailExists } from "./authApi";

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

export type EmailExistsCheckResult = {
  exists: boolean | null;
  needsEmailVerification: boolean;
};

export function useDebouncedEmailExistsCheck(email: string, enabled = true, delayMs = 500) {
  const [exists, setExists] = useState<boolean | null>(null);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const [checking, setChecking] = useState(false);
  const requestIdRef = useRef(0);

  const runCheck = useCallback(async (targetEmail: string): Promise<EmailExistsCheckResult> => {
    const trimmed = targetEmail.trim();
    if (!isValidEmail(trimmed)) {
      setExists(null);
      setNeedsEmailVerification(false);
      return { exists: null, needsEmailVerification: false };
    }

    const requestId = ++requestIdRef.current;
    setChecking(true);
    try {
      const { exists: found, needsEmailVerification: needsVerify } = await checkEmailExists(trimmed);
      if (requestIdRef.current !== requestId) {
        return { exists: null, needsEmailVerification: false };
      }
      setExists(found);
      setNeedsEmailVerification(found && needsVerify);
      return { exists: found, needsEmailVerification: found && needsVerify };
    } catch {
      if (requestIdRef.current !== requestId) {
        return { exists: null, needsEmailVerification: false };
      }
      setExists(null);
      setNeedsEmailVerification(false);
      return { exists: null, needsEmailVerification: false };
    } finally {
      if (requestIdRef.current === requestId) setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setExists(null);
      setNeedsEmailVerification(false);
      setChecking(false);
      return;
    }

    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setExists(null);
      setNeedsEmailVerification(false);
      setChecking(false);
      return;
    }

    setExists(null);
    setNeedsEmailVerification(false);
    const timer = setTimeout(() => {
      void runCheck(email);
    }, delayMs);

    return () => {
      clearTimeout(timer);
    };
  }, [email, enabled, delayMs, runCheck]);

  const checkNow = useCallback(() => runCheck(email), [email, runCheck]);

  return { exists, needsEmailVerification, checking, checkNow };
}
